import * as Sentry from '@sentry/react';
import * as I from 'immutable';

import {ApiFeatureData} from 'app/modules/Remote/Feature';
import * as featureUtils from 'app/utils/featureUtils';
import * as layers from 'app/utils/layers';
import * as layerUtils from 'app/utils/layerUtils';
import * as mapUtils from 'app/utils/mapUtils';

/**
 * Returns a layerKey for the given cursor and feature data, based on a previous
 * layerKey.
 *
 * If the scene is not high-res, just returns the previous layerKey.
 *
 * If the scene is high-res, it will return the previous layerKey if the cursor
 * has a processed image for that layer. If it doesn’t, it will return the first
 * layerKey that there is an image for, or ANY_TRUECOLOR_HIGH_RES if none exist.
 *
 * This is necessary when looking at, say, a Pleiades image and then switching
 * to a date that only has a Maxar image.
 */
export function findAppropriateLayerKey(
  prevLayerKey: string,
  cursor: string,
  featureData: I.ImmutableListOf<ApiFeatureData> | null
): string {
  if (!featureData) {
    return prevLayerKey;
  }

  const isGroupedHighResData = layerUtils.isLayerKeyGroupedHighResData(prevLayerKey);
  const isHighResTruecolor = layerUtils.isLayerKeyHighResTruecolor(prevLayerKey);

  if (!isHighResTruecolor && !isGroupedHighResData) {
    return prevLayerKey;
  }

  const datum = featureData.find((d) => d!.get('date') === cursor);

  if (!datum) {
    return isHighResTruecolor ? layers.ANY_TRUECOLOR_HIGH_RES : prevLayerKey;
  }

  const templateUrls = datum.get('images').get('templateUrls');
  if (templateUrls.get(prevLayerKey)) {
    return prevLayerKey;
  }

  if (isHighResTruecolor) {
    return (
      featureUtils.findFirstProcessedHighResTruecolorLayerKey(datum) ||
      layers.ANY_TRUECOLOR_HIGH_RES
    );
  }

  if (isGroupedHighResData) {
    // Extract layerId from grouped key (e.g., "ANY_NDVI_high-res-data" -> "NDVI")
    const layerId = layerUtils.getLayerIdFromGroupedHighResDataLayerKey(prevLayerKey);
    // Always resolve grouped keys to a specific key when we have a cursor and datum
    // so the image can be rendered. The grouped key is only used for filtering in DateSelect.
    const foundKey = featureUtils.findFirstProcessedHighResDataLayerKey(datum, layerId);
    return foundKey || prevLayerKey;
  }

  return prevLayerKey;
}

function sortImageRefs(imageRefs: mapUtils.MapImageRefs) {
  imageRefs.sort((a, b) => {
    if (a.cursor === null && b.cursor === null) {
      return 0;
    } else if (a.cursor === null) {
      return -1;
    } else if (b.cursor === null) {
      return 1;
    }

    if (a.cursor < b.cursor) {
      return -1;
    } else if (a.cursor > b.cursor) {
      return 1;
    } else {
      return 0;
    }
  });
}

/**
 * This function is called when we cannot find a featureDatum corresponding to a
 * imageRef's layerKey and cursor when calling SET_IMAGE_REFS. It attempts to find
 * the closet other cursor in featureData for the given layerKey.
 *
 * This is helpful in the case of data getting reprocessed and the cursor associated with
 * the new scene in featureData may be slightly off from what the cursor was when the featureData
 * for that scene was saved somewhere like a note.
 */
export function adjustCursor(
  layerKey: string,
  cursor: string,
  featureData: I.ImmutableOf<ApiFeatureData[]>
) {
  // Do not try to adjust the cursor of a highResTruecolor image - since layerKeys may not
  // be specific in this case and we are looking for any highResTruecolor datum, trying
  // to adjust for reprocessed scene may result in incorrectly attributing an image to a
  // different source that was captured near the same time
  if (layerUtils.isLayerKeyHighResTruecolor(layerKey)) {
    return cursor;
  }
  const filteredFeatureData = featureData
    .filter((d) => d!.get('types').includes(layerKey))
    .toList();
  if (filteredFeatureData.isEmpty()) {
    return null;
  }

  const closestCursor = findClosestCursor(cursor, filteredFeatureData, 'date');
  if (closestCursor === null) {
    throw new Error('No cursor found even after we checked');
  }
  return closestCursor;
}

/**
 * Finds and return the closest cursor in featureData for a given cursor
 */
export function findClosestCursor(
  cursor: string,
  filteredFeatureData: I.ImmutableListOf<ApiFeatureData>,
  cursorKey: keyof ApiFeatureData = 'date'
) {
  const cursorMs = Date.parse(cursor);

  let closestDiff = Infinity;
  let closestCursor: string | null = null;

  for (let i = 0; i < filteredFeatureData.size; ++i) {
    const datumCursor = filteredFeatureData.get(i).get(cursorKey) as string;
    const datumCursorMs = Date.parse(datumCursor);
    const datumCursorDiff = Math.abs(cursorMs - datumCursorMs);

    if (datumCursorDiff < closestDiff) {
      closestDiff = datumCursorDiff;
      closestCursor = datumCursor;
    }
  }

  return closestCursor;
}

/**
 * Reconciles imageRefs that came from somewhere specific, such as a note or a
 * link, with the feature data they’ll be rendered against.
 *
 * Cursors may have gotten out of sync with the cursors in featureData (like in
 * the case of reprocessing), so we attempt to adjust them. If we don't find
 * anything close enough, like in the case of a scene being removed, we leave
 * them as-is and allow the raster to show nothing.
 *
 * Also sorts the refs and makes sure they use a precise layerKey if one is
 * available (this is for migrating from e.g. old notes that still reference
 * ALL_high-res-truecolor).
 */
export function resolveImageRefs(
  imageRefs: mapUtils.MapImageRefs,
  featureData: I.ImmutableOf<ApiFeatureData[]> | null
): mapUtils.MapImageRefs {
  const updatedImageRefs = imageRefs.map((r) => {
    if (!r.cursor) {
      return r;
    }
    const updatedLayerKey = findAppropriateLayerKey(r.layerKey, r.cursor, featureData);

    if (featureData && r.cursor) {
      const datum = featureData.find(
        (d) => d!.get('types').includes(updatedLayerKey) && d!.get('date') === r.cursor
      );

      if (!datum) {
        const newCursorWithinRange = adjustCursor(updatedLayerKey, r.cursor, featureData);

        if (newCursorWithinRange) {
          return {layerKey: updatedLayerKey, cursor: newCursorWithinRange};
        } else {
          Sentry.captureException(
            new Error(
              `Missing FeatureData for requested imageRef: ${r.cursor} / ${updatedLayerKey}`
            ),
            {extra: {cursor: r.cursor, layerKey: updatedLayerKey}}
          );
        }
      }
    }
    return {layerKey: updatedLayerKey, cursor: r.cursor};
  }) as mapUtils.MapImageRefs;

  sortImageRefs(updatedImageRefs);

  return updatedImageRefs;
}

/**
 * Correct the cursors to match what’s available in the featureData for the
 * current layer, if it has loaded.
 *
 * Note: does not attempt to preserve consistency of the imageRefs.
 */
export function adjustImageRefs(
  imageRefs: mapUtils.MapImageRefs,
  mode: mapUtils.Mode,
  featureData: I.ImmutableOf<ApiFeatureData[]>
): mapUtils.MapImageRefs {
  // We collapse the high res layer keys together for the featureDataByLayerKey map.
  const layerKeys = imageRefs.map(({layerKey}) =>
    layerUtils.isLayerKeyHighResTruecolor(layerKey) ? layers.ANY_TRUECOLOR_HIGH_RES : layerKey
  );

  // This is us being very pendantic. cursorKey is always going to be "date"
  // right now in Lens.
  const cursorKey = featureUtils.getCursorKeyForLayerKey(null, layerKeys);

  const imageRefFeatureDataByLayerKey = I.Set(layerKeys).reduce(
    (acc, layerKey) =>
      acc!.set(
        layerKey!,
        featureUtils.getProcessedFeatureData(
          featureData.filter(featureUtils.makeLayerKeyPredicate(layerKey!)).toList() ?? I.List(),
          layerKey!
        )
      ),
    I.Map<string, I.ImmutableOf<ApiFeatureData[]>>()
  );

  // Move the cursors to match the closest ones in featureData. This may mean
  // that both cursors map to the same value, in which case we’ll de-dupe later
  // and add back in another cursor (if we’re in compare mode).
  //
  // While a better algorithm might minimize the distance of both cursors at the
  // same time, that level of complication would be unnecessary.
  //
  // We use an Immutable List here because it will guarantee a stable sort order
  // later on.

  let imageRefList = I.List(
    imageRefs.map<mapUtils.MapImageRef>(({layerKey, cursor}) => {
      const normalizedLayerKey = layerUtils.isLayerKeyHighResTruecolor(layerKey)
        ? layers.ANY_TRUECOLOR_HIGH_RES
        : layerKey;
      const featureDataForLayer = imageRefFeatureDataByLayerKey.get(normalizedLayerKey);

      //If the user is switching to Basemap, null out the cursor and let 'er rip
      if (layerKey === layers.NONE) {
        return {layerKey, cursor: null};
      }
      // If the user navigates to a feature and they have no valid scenes for that layerKey,
      // send them to truecolor imagery instead. First checking for high res imagery, but
      // isn't guaranteed for customers outside of the US. In that case default to S2_TRUECOLOR
      // which is global and is assumed to always exist
      else if (featureDataForLayer.isEmpty()) {
        // Start with highResTruecolor
        let newLayerKey = layers.ANY_TRUECOLOR_HIGH_RES;
        let newTruecolorFeatureData = featureUtils.getProcessedFeatureData(
          featureData
            .filter(featureUtils.makeLayerKeyPredicate(layers.ANY_TRUECOLOR_HIGH_RES))
            .toList() ?? I.List(),
          layers.ANY_TRUECOLOR_HIGH_RES
        );

        // Fall back to S2 truecolor if we can't find any highRes
        if (newTruecolorFeatureData.isEmpty()) {
          newLayerKey = layers.S2_TRUECOLOR;
          newTruecolorFeatureData = featureUtils.getProcessedFeatureData(
            featureData.filter(featureUtils.makeLayerKeyPredicate(layers.S2_TRUECOLOR)).toList() ??
              I.List(),
            layers.S2_TRUECOLOR
          );
        }

        // If we still have no data somehow, fall back to the basemap
        if (newTruecolorFeatureData.isEmpty()) {
          return {layerKey, cursor: null};
        }

        // If there's already a cursor set, try to find the closest cursor for our new
        // layerKey. If not, default to the latest date.
        const newCursor = (
          cursor
            ? findClosestCursor(cursor!, newTruecolorFeatureData, cursorKey)
            : newTruecolorFeatureData.first().get(cursorKey)
        ) as string;

        return {
          layerKey: findAppropriateLayerKey(newLayerKey, newCursor, newTruecolorFeatureData),
          cursor: newCursor,
        };
      } else if (cursor === null) {
        const updatedCursor = featureDataForLayer.first().get(cursorKey) as string;

        return {
          layerKey: findAppropriateLayerKey(layerKey, updatedCursor, featureData),
          cursor: updatedCursor,
        };
      }

      const closestCursor = findClosestCursor(cursor, featureDataForLayer, cursorKey);

      if (closestCursor === null) {
        // featureDataForLayerKey check from before
        throw new Error('No cursor found even after we checked');
      }

      return {
        layerKey: findAppropriateLayerKey(layerKey, closestCursor, featureData),
        cursor: closestCursor,
      };
    })
  );

  // Check and make sure that the above "find the closest cursor" didn’t leave
  // us with the same cursor and layerKey for two images. If so, remove one and
  // let the compare mode correction mode add it back in.
  if (imageRefList.size === 2) {
    const first = imageRefList.get(0);
    const second = imageRefList.get(1);

    if (first.layerKey === second.layerKey && first.cursor === second.cursor) {
      imageRefList = imageRefList.take(1).toList();
    }
  }

  // Adjust the cursors to match the mode.
  if (mode === mapUtils.MODES.VIEW && imageRefList.size > 1) {
    imageRefList = imageRefList.takeLast(1).toList();
  } else if (mode === mapUtils.MODES.COMPARE && imageRefList.size === 1) {
    const {layerKey, cursor} = imageRefList.first();
    const featureDataForLayer = featureUtils.getProcessedFeatureData(
      featureData.filter(featureUtils.makeLayerKeyPredicate(layerKey)).toList() ?? I.List(),
      layerKey
    );

    if (cursor === null) {
      // If we get here it’s because there’s no valid scenes in the feature data
      // for this layerKey. The frontend should prevent this but we should make
      // sure we handle it without crashing.
      imageRefList = imageRefList.push({
        layerKey: layerUtils.isLayerKeyHighResTruecolor(layerKey)
          ? layers.ANY_TRUECOLOR_HIGH_RES
          : layerKey,
        cursor: null,
      });
    } else {
      const currentCursorIdx = featureDataForLayer.findIndex((d) => d!.get(cursorKey) === cursor);

      if (currentCursorIdx === -1) {
        throw new Error('Cursor not found in featureDataForLayer after reconcilliation');
      }

      // "prev" because we’re sorted descending
      const prevCursorIdx = currentCursorIdx + 1;
      const nextCursorIdx = currentCursorIdx - 1;

      if (prevCursorIdx < featureDataForLayer.size) {
        const newCursor = featureDataForLayer.get(prevCursorIdx).get(cursorKey) as string;

        imageRefList = imageRefList.push({
          layerKey: findAppropriateLayerKey(layerKey, newCursor, featureData),
          cursor: newCursor,
        });
      } else if (nextCursorIdx >= 0) {
        const newCursor = featureDataForLayer.get(nextCursorIdx).get(cursorKey) as string;

        // The current cursor is the last one in the FeatureData collection, so
        // let’s add the cursor before it. Sorting will be handled later.
        imageRefList = imageRefList.push({
          layerKey: findAppropriateLayerKey(layerKey, newCursor, featureData),
          cursor: newCursor,
        });
      } else {
        imageRefList = imageRefList.push({
          layerKey: layerUtils.isLayerKeyHighResTruecolor(layerKey)
            ? layers.ANY_TRUECOLOR_HIGH_RES
            : layerKey,
          cursor: null,
        });
      }
    }
  }

  // using the ol’ “ISO8601 dates are sortable lexographically” hack.
  imageRefList = imageRefList.sortBy((ref) => ref!.cursor).toList();

  return imageRefList.toArray() as mapUtils.MapImageRefs;
}
